import { ErrorAlert } from "@/components/error";
import { PendingBillingComponent } from "@/components/pending";
import { authClient } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { AuthUIContext } from "@papier/auth-ui";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { Check, X } from "lucide-react";
import { use, useEffect } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/organization/billing")({
  component: RouteComponent,
});

function RouteComponent() {
  const { useNavigate, useSearch } = Route;
  const navigate = useNavigate();
  const { plan } = useSearch();
  const {
    hooks: { useHasPermission },
  } = use(AuthUIContext);

  const { data: activeOrganization } = authClient.useActiveOrganization();
  const { data: hasPermission, isPending: isPermissionPending } = useHasPermission({
    organizationId: activeOrganization?.id,
    permissions: {
      organization: ["update"],
    },
  });

  const {
    data: subscription,
    isPending,
    error,
  } = useQuery(trpc.subscriptions.getActiveSubscription.queryOptions());

  const {
    mutate: createBilling,
    isPending: isBillingPending,
    isSuccess: isBillingSuccess,
  } = useMutation(
    trpc.subscriptions.createBillingPortalSession.mutationOptions({
      onSuccess: (url) => {
        navigate({ href: url });
      },
      onError: (error) => {
        toast.error("Failed to create billing session", {
          description: error.message,
        });
      },
    }),
  );

  const {
    mutate: createCheckout,
    isPending: isCheckoutPending,
    isSuccess: isCheckoutSuccess,
  } = useMutation(
    trpc.subscriptions.createCheckoutSession.mutationOptions({
      onSuccess: (url) => {
        navigate({ href: url || undefined });
      },
      onError: (error) => {
        toast.error("Failed to create checkout session", {
          description: error.message,
        });
      },
    }),
  );

  useEffect(() => {
    if (plan && subscription?.plan !== "pro") {
      createCheckout();
    }
  }, [plan, subscription?.plan]);

  if (isPending || isPermissionPending) {
    return <PendingBillingComponent />;
  }
  if (error) {
    return <ErrorAlert error={error.message} />;
  }

  const isProPlan = subscription?.plan === "pro";
  return (
    <main className="grid gap-4 md:gap-6">
      <Card className="p-0">
        <CardHeader className="pt-6">
          <CardTitle className="text-lg md:text-xl">Billing & Subscription</CardTitle>
          <CardDescription>View your current subscription plan and payment method</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <section className="bg-secondary/50 grid gap-5 rounded-lg border p-4">
            <div className="flex justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">
                    <span className="capitalize">{subscription?.plan || "free"}</span> plan
                  </h3>
                </div>
                {isProPlan && subscription.periodEnd ? (
                  <>
                    {subscription.cancelAtPeriodEnd ? (
                      <div className="text-muted-foreground text-sm">
                        Scheduled for cancellation: {format(subscription.periodEnd, "MMM d, yyyy")}
                      </div>
                    ) : (
                      <div className="text-muted-foreground text-sm">
                        Next Billing: {format(subscription.periodEnd, "MMM d, yyyy")}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-muted-foreground text-sm">No billing required</div>
                )}
              </div>

              {isProPlan ? (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-lg font-semibold">$20</span>
                  <span className="text-muted-foreground text-sm">Per month</span>
                </div>
              ) : (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-lg font-semibold">$0</span>
                  <span className="text-muted-foreground text-sm">Per month</span>
                </div>
              )}
            </div>
          </section>

          <section className="grid gap-3">
            <h4 className="font-medium">Plan Features</h4>
            {isProPlan ? (
              <div className="grid gap-3 text-sm md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Etc</span>
                </div>
              </div>
            ) : (
              <div className="grid gap-3 text-sm md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Basic Editor</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="size-4 text-green-600" />
                  <span>Limited access</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="size-4 text-red-600" />
                  <span className="text-muted-foreground">Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="size-4 text-red-600" />
                  <span className="text-muted-foreground">Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="size-4 text-red-600" />
                  <span className="text-muted-foreground">Etc</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="size-4 text-red-600" />
                  <span className="text-muted-foreground">Etc</span>
                </div>
              </div>
            )}
          </section>
        </CardContent>
        <CardFooter className="bg-sidebar py-4! rounded-b-xl border-t">
          <div className="text-muted-foreground text-center text-xs md:text-start md:text-sm">
            Manage your subscription and billing information.
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button asChild variant="outline">
              <a href="https://usepapier.io/pricing" target="_blank" rel="noopener noreferrer">
                View plans
              </a>
            </Button>
            {isProPlan ? (
              <Button
                disabled={!hasPermission?.success}
                isLoading={isBillingPending || isBillingSuccess}
                onClick={() => createBilling()}
              >
                Manage Plan
              </Button>
            ) : (
              <Button
                disabled={!hasPermission?.success}
                isLoading={isCheckoutPending || isCheckoutSuccess}
                onClick={() => createCheckout()}
              >
                Upgrade Plan
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>
    </main>
  );
}
